import { cardFace, cardNumber } from "@/content/cards"
import { cardTint } from "./cardTint"
import type { CardId } from "@/types"
import styles from "@/styles/components/CardThumb.module.css"

/*
 * A card reduced to its family colour and number.
 *
 * Used everywhere a card is a token rather than something to read: the board's
 * quadrants, the hand slots, the deep-dive strip, the pick monitor, the discard
 * line. At these sizes the statement is unreadable, so printing it would only
 * add noise — the tint places the family and the number names the card, and the
 * `title` carries the rest for anyone who needs it.
 *
 * A `<button>` when it does something, a `<span>` when it does not: a tile that
 * only reports state must not land in the tab order.
 */

interface CardThumbProps {
  cardId: CardId
  size?: "xs" | "sm"
  /**
   * Prints the card's statement on the tile. Only for a tile wide enough to
   * carry it — the hand's landscape slots — and off everywhere else, where the
   * tint and the number are the whole point.
   */
  withStatement?: boolean
  onClick?: () => void
  /** Overrides the default "name — statement" hover text. */
  title?: string
  /** Dimmed and non-interactive: this card is being carried elsewhere. */
  ghosted?: boolean
  /**
   * Makes the tile a grab handle — see `useBoardDrag`, which owns the gesture.
   * It is a pointer drag rather than HTML5 `draggable` because no browser but
   * Chrome will start a native drag on a button, and none of them do it on touch.
   */
  onPointerDown?: (event: React.PointerEvent) => void
  className?: string
}

export default function CardThumb({
  cardId,
  size = "sm",
  withStatement,
  onClick,
  title,
  ghosted,
  onPointerDown,
  className,
}: CardThumbProps) {
  const face = cardFace(cardId)
  const { tint, ink, family } = cardTint(cardId)

  const label = title ?? (face ? `${face.name} — ${face.statement}` : cardNumber(cardId))
  const classes = [
    styles.thumb,
    styles[size],
    withStatement ? styles.stated : "",
    ghosted ? styles.ghosted : "",
    className,
  ]
    .filter(Boolean)
    .join(" ")
  const style = { background: tint, color: ink }

  const body = (
    <>
      {withStatement && face ? <span className={styles.statement}>{face.statement}</span> : null}
      <span className={styles.number}>{cardNumber(cardId)}</span>
    </>
  )

  if (!onClick && !onPointerDown) {
    return (
      <span className={classes} data-family={family} style={style} title={label}>
        {body}
      </span>
    )
  }

  return (
    <button
      type="button"
      className={classes}
      data-family={family}
      data-grab={onPointerDown ? "" : undefined}
      style={style}
      title={label}
      aria-label={label}
      onPointerDown={onPointerDown}
      onClick={onClick}
    >
      {body}
    </button>
  )
}
